import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { PricingApi } from "../../apis"
import { BuyerPromotionsView, type KindFilter } from "../../components/buyer-sales/promotions/BuyerPromotionsView"
import { useBuyerSuppliers, useSupplierChoice } from "../../components/buyer-sales/quick-order/use-buyer-supplier"
import { SupplierSelect } from "../../components/buyer-sales/supplier-select"
import { TableFrame } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState } from "../../components/ui"
import { renderBusinessName } from "../../utils/render-business-name"

/** «Урамшуулал» (B27, canvas OrdB_PromoPage): active promotions of one supplier that reach this buyer. */
export function BuyerSalesPromotions() {
  const { t } = useTranslation(["order", "common", "supplierSales"])
  const navigate = useNavigate()
  const ORD_LIST = usePermission("ORD_LIST")
  const { suppliers, isLoading } = useBuyerSuppliers(ORD_LIST.hasAccess)
  const { supplierId, supplier, setSupplierId } = useSupplierChoice(suppliers)
  const [kind, setKind] = React.useState<KindFilter>("ALL")

  const { data, error, isLoading: loadingRows, mutate } = useSWR(
    supplierId ? ["buyer-promotions", supplierId] : null,
    () => PricingApi.buyerPromotions(supplierId),
    { revalidateOnFocus: false },
  )

  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
    { to: "/buyer-sales/new", label: t("order:catalog.title") },
    { label: t("order:catalog.promotions.title") },
  ]
  const title = t("order:catalog.promotions.title")

  if (!ORD_LIST.hasAccess) {
    return (
      <PageLayout title={title} breadcrumb={breadcrumb} noFrame>
        <DeniedCard permission="ORD_LIST" />
      </PageLayout>
    )
  }

  if (isLoading || suppliers.length === 0) {
    return (
      <PageLayout title={title} breadcrumb={breadcrumb} noFrame>
        <TableFrame>
          {isLoading ? (
            <ListState state="loading" />
          ) : (
            <ListState
              state="empty"
              title={t("supplierSales:customer.noSupplier")}
              description={t("supplierSales:customer.noSupplierDescription")}
              action={{ label: t("supplierSales:customer.backToList"), onClick: () => navigate("/buyer-sales") }}
            />
          )}
        </TableFrame>
      </PageLayout>
    )
  }

  const status = (error as { statusCode?: number } | undefined)?.statusCode
  const state = error ? (status === 403 ? "denied" : "error") : loadingRows && !data ? "loading" : "data"

  return (
    <PageLayout title={title} breadcrumb={breadcrumb} noFrame>
      <BuyerPromotionsView
        rows={data?.rows || []}
        state={state}
        supplierName={supplier ? renderBusinessName(supplier, "") : ""}
        supplierPicker={<SupplierSelect suppliers={suppliers} value={supplierId} onChange={setSupplierId} />}
        kind={kind}
        onKindChange={setKind}
        onRetry={() => mutate()}
      />
    </PageLayout>
  )
}
